import { useQuery } from "@tanstack/react-query";
import { RefreshCw, Siren, Wrench, Activity, OctagonAlert } from "lucide-react";
import { get } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtHeure, todayIso } from "@/lib/format";
import { useAcquittements, interventionKey } from "@/lib/alertes";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiTile } from "@/components/common/KpiTile";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/States";
import { AlertesActives, InterventionsATraiter } from "@/components/alertes/AlertesPanels";
import { HistoriqueAlertes } from "@/components/alertes/HistoriqueAlertes";

const REFRESH_MS = 10000;

export default function AlertesPage() {
  const { entrepriseId, user } = useAuth();
  const ready = user.role !== "superadmin" || !!entrepriseId;
  const { acks, acquitter, retirer, purger, mode } = useAcquittements();
  const dash = useQuery({ queryKey: ["dashboard", todayIso(), entrepriseId], queryFn: () => get("/dashboard", { date_jour: todayIso() }), refetchInterval: REFRESH_MS, enabled: ready });

  const alertes = dash.data?.alertes || [];
  const interventions = dash.data?.interventions || [];
  const lignes = (dash.data?.lignes || []).filter((l) => l.statut !== "hors_service");
  const rouges = alertes.filter((a) => a.niveau === "rouge").length;
  const enAttente = interventions.filter((r) => !acks[interventionKey(r)]);
  const critiques = enAttente.filter((r) => r.priorite === "critique" || r.priorite === "urgente").length;
  const lignesTouchees = new Set([...alertes.map((a) => a.ligne), ...enAttente.map((r) => r.ligne)]).size;
  const arretsNP = lignes.filter((l) => l.statut === "arret_non_planifie").length;

  return (
    <div data-testid="alertes-page" className="space-y-3">
      <PageHeader crumbs={[{ label: "ProdTrack" }, { label: "Pilotage" }, { label: "Alertes & interventions" }]} title="Alertes & interventions"
        subtitle={dash.data ? `Temps réel · rafraîchi toutes les ${REFRESH_MS / 1000} s · dernière mise à jour ${fmtHeure(new Date(dash.dataUpdatedAt).toISOString())}` : "Chargement…"}
        actions={<button data-testid="alertes-refresh" onClick={() => dash.refetch()} className="h-7 px-2 border border-border rounded-sm text-[11px] flex items-center gap-1 hover:bg-muted"><RefreshCw className={cn("h-3 w-3", dash.isFetching && "animate-spin")} />Actualiser</button>} />

      {!ready ? <EmptyState title="Sélectionnez une entreprise" hint="En tant que superadmin, choisissez l'entreprise à surveiller dans la barre supérieure." />
        : dash.isLoading ? <LoadingState rows={6} /> : dash.error ? <ErrorState error={dash.error} onRetry={dash.refetch} /> : (<>
        <div data-testid="alertes-kpis" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
          <KpiTile testId="kpi-alertes" label="Alertes actives" value={alertes.length} icon={Siren} tone={rouges ? "crit" : alertes.length ? "warn" : "ok"} sub={`${rouges} critique(s) · ${alertes.length - rouges} vigilance`} />
          <KpiTile testId="kpi-interventions" label="Interventions à traiter" value={enAttente.length} icon={Wrench} tone={critiques ? "crit" : enAttente.length ? "warn" : "ok"} sub={`${critiques} critique(s)`} />
          <KpiTile testId="kpi-acquittees" label="Acquittées" value={interventions.length - enAttente.length} sub={mode === "serveur" ? "partagées · tous les utilisateurs" : "démo · ce navigateur uniquement"} />
          <KpiTile testId="kpi-lignes-touchees" label="Lignes concernées" value={`${lignesTouchees} / ${lignes.length}`} icon={Activity} tone={lignesTouchees ? "warn" : "ok"} sub="lignes actives" />
          <KpiTile testId="kpi-arrets-np" label="Arrêts non planifiés" value={arretsNP} icon={OctagonAlert} tone={arretsNP ? "crit" : "ok"} sub="en cours maintenant" />
          <KpiTile testId="kpi-action" label="Action prioritaire" value={dash.data.resume?.action ? "1" : "0"} sub={dash.data.resume?.action || "Aucune action recommandée"} className="col-span-2 md:col-span-1" />
        </div>

        {dash.data.resume?.action && (
          <div data-testid="alertes-action" className="panel px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs border-l-2 border-l-primary">
            <span className="eyebrow">Action recommandée</span><span className="font-medium">{dash.data.resume.action}</span>
          </div>
        )}

        <InterventionsATraiter interventions={interventions} acks={acks} acquitter={acquitter} retirer={retirer} purger={purger} user={user} mode={mode} />
        <AlertesActives alertes={alertes} lignes={lignes.map((l) => l.ligne)} />
        <HistoriqueAlertes lignes={lignes} />
      </>)}
    </div>
  );
}
